import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";

/**
 * FormError (CHP-522) — the single inline field-error line. Renders nothing
 * when there's no message, so call sites can pass an optional error directly.
 * Replaces the `<p role="alert" className="text-sm text-destructive">` string
 * re-typed across ~72 files (audit X6).
 *
 * Forwards standard `<p>` attributes (e.g. `id`, `data-testid`) so a field can
 * reference the message via `aria-describedby`/`aria-errormessage` (CHP-805).
 *
 * CHP-781: pass `inline` for a per-field error line when a form highlights
 * several missing fields at once. An `inline` line DROPS `role="alert"` so the
 * form keeps a single top-level summary as the live alert region and the
 * per-field lines are linked via `aria-describedby` instead (avoids a screen
 * reader firing one alert per missing field). `id` flows through `...rest`, so
 * the per-field line can be referenced by its input's `aria-describedby`.
 */
export function FormError({
  children,
  className,
  inline = false,
  ...rest
}: ComponentPropsWithoutRef<"p"> & { inline?: boolean }) {
  if (!children) return null;
  return (
    <p
      role={inline ? undefined : "alert"}
      className={cn("text-sm text-destructive", className)}
      {...rest}
    >
      {children}
    </p>
  );
}
